/**
 * Build & submit: which task dialog a folder gets.
 *
 * Since 2026-10 ecomm tasks come from two Asana projects with two delivery
 * rules. Audience+ posts the Drive and Preview links as a comment on the task
 * (dialogDeliveryTask.js); ACS Ads Manager hands each round off on a subtask —
 * field, comment, assignee, section — and fixes the file names in the ticket
 * (dialogDeliveryTaskAdsManager.js).
 * The folder's bound Asana task decides — its project is the one fact both
 * sides agree on — so the dialog asks before it opens either.
 *
 * Unbound folders and legacy bindings whose task no longer exists default to
 * Audience+. Failed lookups also use Audience+ when no known workflow is
 * cached; a cached Ads Manager task keeps its own dialog during an outage.
 *
 * Which folder is on screen, and the memory the tasks are kept in, live here
 * too. The body is keyed by the folder, so switching one UNMOUNTS the task
 * being left and mounts the next from `memory` — nothing can survive the switch
 * by accident. The memory outlives the body because builds and deliveries
 * outlive the dialog: a folder's task has to be there when it is opened again.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { asanaAdsManagerContext } from "../../modules/snapshotEditorApi";
import { normalizeFolderPath } from "../../modules/snapshotTree";
import { AudiencePlusTaskDialog } from "./dialogDeliveryTask";
import AdsManagerTaskDialog from "./dialogDeliveryTaskAdsManager";
import { PANEL_BG } from "./panelPalette";

/** The one Asana gid every snapshot of a folder agrees on, or null. */
function folderGid(snapshots, folder) {
  const target = normalizeFolderPath(folder || "");
  const gids = _.uniq(
    (snapshots || [])
      .filter((snapshot) => normalizeFolderPath(snapshot.exportFolder || "") === target)
      .map((snapshot) => snapshot.asanaGid)
      .filter(Boolean)
  );
  return gids.length === 1 ? gids[0] : null;
}

/**
 * The workflow of a folder's task, asked of Asana each time the dialog opens.
 *
 * Asked again on every open rather than once: an Ads Manager ticket keeps
 * changing underneath — the Drive folder appears minutes after the claim, and
 * ACS opens a new subtask with every feedback round. The last answer stays on
 * screen while the next one loads, so a reopen does not flash.
 */
function useTaskWorkflow(baseID, open, folder, gid) {
  const key = JSON.stringify([baseID, folder, gid]);
  const answers = useRef(new Map());
  const latestRequest = useRef(0);
  const activeKey = useRef(null);
  const [request, setRequest] = useState(null);
  activeKey.current = open ? key : null;

  const refresh = useCallback(async () => {
    if (!open || !folder || !gid || activeKey.current !== key) return;
    const requestId = ++latestRequest.current;
    const isCurrent = () => activeKey.current === key && latestRequest.current === requestId;
    setRequest({ key, loading: true, error: null });
    try {
      const response = await asanaAdsManagerContext(baseID, folder);
      if (!isCurrent()) return;
      if (!response.ok) {
        setRequest({
          key,
          loading: false,
          error: _.get(response, "error.message") ||
            _.get(response, "error.error") ||
            "The Asana task could not be looked up.",
        });
        return;
      }
      answers.current.set(key, { ...response.data, gid });
      setRequest({ key, loading: false, error: null });
    } catch (err) {
      if (isCurrent()) {
        setRequest({ key, loading: false, error: err.message || "The Asana task could not be looked up." });
      }
    }
  }, [baseID, open, folder, gid, key]);

  useEffect(() => {
    refresh();
    // Folder changes, closing and unmounting invalidate outstanding requests.
    return () => { latestRequest.current += 1; };
  }, [refresh]);

  const context = answers.current.get(key);
  const loading = request?.key === key && request.loading;
  const error = request?.key === key ? request.error : null;

  if (!folder || !gid) return { kind: "audienceplus" };
  if (context) {
    return {
      kind: context.adsManager ? "adsmanager" : "audienceplus",
      context,
      loading,
      error,
      refresh,
    };
  }
  return { kind: error ? "audienceplus" : "checking", loading, error, refresh };
}

export default function DialogDeliveryTask({ editor, open, folderPath, onClose, createSnackbar }) {
  const memory = useRef({});
  const [selectedFolder, setSelectedFolder] = useState(folderPath || "");

  // Each open shows the folder it was opened on, never the one left on screen.
  //
  // WHILE RENDERING, not in an effect. An effect runs after the commit, so
  // opening a second folder let one commit through with the FIRST folder's
  // body mounted and `open` already true — and that body does what an open
  // body does: it takes its folder's finished build off the job board. The
  // build it took belonged to a folder nobody had opened, and the result never
  // reached the memory either, because the body unmounted in the same flush as
  // the state that would have saved it.
  //
  // Which is the bug this was reported for: build several folders at once,
  // open one of them, and another finished one loses its mark in the tree (the
  // job is gone from the board) and opens as though it was never built.
  //
  // Setting state here instead re-renders this component before anything is
  // committed, so the wrong folder's body is never mounted and open at once.
  const openedFor = useRef(null);
  if (!open) openedFor.current = null;
  else if (openedFor.current !== folderPath) {
    openedFor.current = folderPath;
    if (selectedFolder !== (folderPath || "")) setSelectedFolder(folderPath || "");
  }

  const gid = useMemo(() => folderGid(editor.snapshots, selectedFolder), [editor.snapshots, selectedFolder]);
  const workflow = useTaskWorkflow(editor.baseID, open, selectedFolder, gid);

  if (workflow.kind === "checking") {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ style: { backgroundColor: PANEL_BG } }}>
        <DialogTitle>Build &amp; submit task</DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <CircularProgress size={18} />
            <Typography style={{ fontSize: "13px" }}>Checking which Asana project this task is in…</Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>

        </DialogActions>
      </Dialog>
    );
  }

  const shared = {
    editor,
    open,
    folder: selectedFolder,
    memory,
    onPickFolder: setSelectedFolder,
    onClose,
    createSnackbar,
  };

  if (workflow.kind === "adsmanager") {
    return (
      <AdsManagerTaskDialog
        key={`adsmanager:${selectedFolder}`}
        {...shared}
        context={workflow.context}
        isRefreshing={workflow.loading}
        refreshError={workflow.error}
        onRefresh={workflow.refresh}
      />
    );
  }
  return <AudiencePlusTaskDialog key={selectedFolder} {...shared} workflowWarning={workflow.error} />;
}
